import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { imageProblem } from "./supportLogic";

// Поле «Написать в поддержку» (у администратора — «Ответить пользователю»):
// скрепка с единственным пунктом «Изображение», текст и кнопка отправки, которая
// появляется, когда есть что отправлять (эталон: .co-comp). Картинка уходит
// сразу, отдельной репликой, а набранный текст остаётся в поле — как в эталоне.

const MAX_TEXT = 4000;
const MAX_HEIGHT = 138;

export function Composer({
  placeholder,
  onSend,
}: {
  placeholder: string;
  /** Отправить реплику; вернуть текст ошибки или null, если ушла. */
  onSend: (text: string, image?: File) => Promise<string | null>;
}) {
  const [draft, setDraft] = useState("");
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const hasDraft = draft.trim() !== "";

  // Поле растёт вместе с текстом, но не выше MAX_HEIGHT.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "38px";
    el.style.height = `${Math.min(MAX_HEIGHT, Math.max(38, el.scrollHeight))}px`;
  }, [draft]);

  // Меню скрепки закрывается щелчком мимо.
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  async function run(text: string, image?: File) {
    if (busy) return;
    setBusy(true);
    setError(null);
    const problem = await onSend(text, image);
    setBusy(false);
    if (problem) setError(problem);
    else if (!image) setDraft("");
  }

  function pickImage(f: File | undefined) {
    if (!f) return;
    const bad = imageProblem(f);
    if (bad) {
      setError(bad);
      return;
    }
    void run("", f);
  }

  return (
    <div className="sup-comp">
      {error && (
        <div className="sup-err" role="alert">
          {error}
        </div>
      )}
      <div className="sup-comp-row">
        <div className="sup-clipw" ref={wrap}>
          <button
            type="button"
            className={"sup-clip" + (menu ? " sup-clip--on" : "")}
            title="Прикрепить"
            aria-label="Прикрепить"
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57a4 4 0 0 1 5.66 5.66l-8.58 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          {menu && (
            <div className="sup-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  file.current?.click();
                }}
              >
                <span className="sup-menu-ic">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3.5 6.5h4l1.4-2h6.2l1.4 2h4v12h-17zM12 15.6a3.4 3.4 0 100-6.8 3.4 3.4 0 000 6.8z" />
                  </svg>
                </span>
                Изображение
              </button>
            </div>
          )}
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(e) => {
              pickImage(e.target.files?.[0]);
              // Тот же файл, выбранный снова, иначе не сработал бы.
              e.target.value = "";
            }}
          />
        </div>
        <textarea
          ref={area}
          className="sup-ta"
          rows={1}
          maxLength={MAX_TEXT}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter отправляет, Shift+Enter — перенос строки. Во время набора
            // через IME Enter подтверждает слово, а не сообщение.
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (hasDraft) void run(draft);
            }
          }}
        />
        <div className="sup-sendw">
          {hasDraft && (
            <button
              type="button"
              className="sup-send"
              title="Отправить"
              aria-label="Отправить"
              disabled={busy}
              onClick={() => void run(draft)}
            >
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="currentColor" />
                <path d="M12 17.2V7.4M7.9 11.5L12 7.2l4.1 4.3" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
